import { useStore } from 'effector-react'
import { Parameter } from 'features/credit-filter-form/components/Parameter'
import {
  $fetching,
  $totalCount,
  setChosenCreditFilterValues,
  setCreditFilterEnabled,
  setCreditFilterValues,
  setCreditFormOpened
} from 'features/credit-filter-form/model'
import { CreditFilterFormProps } from 'features/credit-filter-form/types'
import { CarsFoundForms } from 'features/filter/components'
import { useCarsFoundText } from 'lib/use-cars-found-text'
import { FC, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAVNConfig } from 'stages/AVN'
import styled from 'styled-components'
import { Button } from 'ui/button'
import { Loader } from 'ui/loader'
import { PopupForm } from 'ui/popup-form'

import { getFromStorage, storage } from '../../../lib/storage'
import { snapToBounds } from '../../../ui/range-slider/useRangeValue'
import { $maxPrice } from '../../cars-list/model'
import { $tradeInEnabled, $tradeInPrice } from '../../smart-contract/model'

export const CreditFilterForm: FC<{
  onClose(): void
  show: boolean
}> = ({ onClose, show }) => {
  const {
    maxVisibleCount,
    i18n: { carsFoundForms },
    creditFilterForm: { i18n }
  } = useAVNConfig()
  const form = useCreditFilterForm(useStore($maxPrice), useStore($tradeInEnabled), useStore($tradeInPrice))
  const carsFoundText = useCarsFoundText(form?.carsFound || 0, maxVisibleCount || 100, carsFoundForms)

  useEffect(() => {
    setCreditFormOpened(!!form && show)
  }, [show, form])

  if (form) {
    const { creditTerms, creditPayment, buybackPayment, applyValues, initialFee, fetching } = form

    return createPortal(
      <PopupForm width="49rem" title={i18n.title} onClose={onClose} show={show}>
        <Description>{i18n.description}</Description>
        <Parameters>
          <Parameter
            title={i18n.initialFee}
            min={initialFee.range[0]}
            max={initialFee.range[1]}
            value={initialFee.value}
            onSubmit={initialFee.update}
          />
          <Parameter
            title={i18n.buybackPayment}
            min={buybackPayment.range[0]}
            max={buybackPayment.range[1]}
            value={buybackPayment.value}
            onSubmit={buybackPayment.update}
          />
          <Parameter
            title={i18n.creditPayment}
            min={creditPayment.range[0]}
            max={creditPayment.range[1]}
            value={creditPayment.value}
            onSubmit={creditPayment.update}
          />
          <Parameter
            title={i18n.period}
            min={creditTerms.range[0]}
            max={creditTerms.range[1]}
            step={12}
            onSubmit={creditTerms.update}
            value={creditTerms.value}
          />
        </Parameters>
        <CarsFoundText>
          {fetching ? (
            <>
              {i18n.found}:
              <LoaderWrapper>
                <Loader size="small" />
              </LoaderWrapper>
            </>
          ) : (
            carsFoundText
          )}
        </CarsFoundText>
        <Button
          disabled={fetching}
          size="big"
          onClick={() => {
            onClose()
            applyValues()
          }}
        >
          {i18n.apply}
        </Button>
      </PopupForm>,
      document.body
    )
  }

  return null
}

const useCreditFilterForm = (maxPrice: number | null, tradeInEnabled: boolean | null, tradeInPrice: number | null) => {
  const [initialFeeRange, setInitialFeeRange] = useState<[number, number] | null>(null)
  const [buybackPaymentRange, setBuybackPaymentRange] = useState<[number, number] | null>(null)
  const [creditPaymentRange, setCreditPaymentRange] = useState<[number, number] | null>(null)
  const [creditTermsRange, setCreditTermsRange] = useState<[number, number] | null>(null)

  const [initialFee, setInitialFee] = useState<number | null>(null)
  const [buybackPayment, setBuybackPayment] = useState<number | null>(null)
  const [creditPayment, setCreditPayment] = useState<number | null>(null)
  const [creditTerms, setCreditTerms] = useState<number | null>(null)

  const carsFound = useStore($totalCount)

  const fetching = useStore($fetching)

  const ranges = useMemo(() => {
    return initialFeeRange !== null &&
      buybackPaymentRange !== null &&
      creditPaymentRange !== null &&
      creditTermsRange !== null
      ? {
          buybackPaymentRange,
          creditPaymentRange,
          creditTermsRange,
          initialFeeRange
        }
      : null
  }, [initialFeeRange, buybackPaymentRange, creditPaymentRange, creditTermsRange])

  const formState = useMemo(() => {
    return initialFee !== null && buybackPayment !== null && creditPayment !== null && creditTerms !== null
      ? {
          creditTerms: creditTerms,
          initialFee: initialFee,
          creditPayment: creditPayment,
          buybackPayment: buybackPayment
        }
      : null
  }, [initialFee, buybackPayment, creditPayment, creditTerms])

  const api = useMemo(() => {
    if (!formState || !ranges) return null

    return {
      carsFound,
      fetching,
      initialFee: {
        range: ranges.initialFeeRange,
        value: formState.initialFee,
        update: setInitialFee
      },
      buybackPayment: {
        range: ranges.buybackPaymentRange,
        value: formState.buybackPayment,
        update: setBuybackPayment
      },
      creditPayment: {
        range: ranges.creditPaymentRange,
        value: formState.creditPayment,
        update: setCreditPayment
      },
      creditTerms: {
        range: ranges.creditTermsRange,
        value: formState.creditTerms,
        update: setCreditTerms
      },
      applyValues: () => {
        setChosenCreditFilterValues(formState)
        setCreditFilterEnabled(true)
      }
    }
  }, [formState, ranges, carsFound, fetching])

  // Создаём границы ползунков когда становится известен maxPrice
  useEffect(() => {
    if (maxPrice !== null && tradeInEnabled !== null) {
      setCreditPaymentRange([0, Math.round(maxPrice / 12)])
      setInitialFeeRange([tradeInEnabled && tradeInPrice ? tradeInPrice : 0, Math.round(0.95 * maxPrice)])
      setBuybackPaymentRange([
        0,
        tradeInEnabled && tradeInPrice ? maxPrice - tradeInPrice : Math.round(0.95 * maxPrice)
      ])
      setCreditTermsRange([12, 60])
    }
  }, [maxPrice, tradeInEnabled, tradeInPrice])

  // Если значения фильтра ещё неизвестны устанавливаем их из значений ползунков по формулам
  useEffect(() => {
    const stored = getFromStorage('creditFilterFormState')
    if (stored) {
      setInitialFee(stored.initialFee)
      setBuybackPayment(stored.buybackPayment)
      setCreditPayment(stored.creditPayment)
      setCreditTerms(stored.creditTerms)
    } else if (maxPrice !== null) {
      if (initialFeeRange && initialFee === null) setInitialFee(Math.round(initialFeeRange[1] * 0.25))
      if (buybackPaymentRange && buybackPayment === null) setBuybackPayment(Math.round(0.45 * buybackPaymentRange[1]))
      if (creditPaymentRange && creditPayment === null) setCreditPayment(Math.round((0.3 * maxPrice) / 12))
      if (creditTerms === null) setCreditTerms(24)
    }
  }, [initialFeeRange, buybackPaymentRange, creditTermsRange, creditPaymentRange, maxPrice])

  // Привязываем выходящие за границы значения (может произойти при пересчётах трейд-ина, его включениях и выключениях)
  useEffect(() => {
    if (initialFeeRange && initialFee !== null)
      setInitialFee(snapToBounds(initialFeeRange[0], initialFeeRange[1], initialFee))

    if (buybackPaymentRange && buybackPayment !== null)
      setBuybackPayment(snapToBounds(buybackPaymentRange[0], buybackPaymentRange[1], buybackPayment))

    if (creditPaymentRange && creditPayment !== null)
      setCreditPayment(snapToBounds(creditPaymentRange[0], creditPaymentRange[1], creditPayment))
  }, [initialFeeRange, buybackPaymentRange, initialFee, buybackPayment, creditPayment, creditPaymentRange])

  // При изменениях значения стейта устанавливаем соответствующие значения в сторе
  useEffect(() => {
    if (formState) {
      setCreditFilterValues(formState)
    }
  }, [formState])

  return api
}

const Description = styled.p`
  margin-top: 0;
  margin-bottom: 3.2rem;
  ${({ theme }) => theme.textStyles.p1};
`

const Parameters = styled.div``

const CarsFoundText = styled.div`
  display: flex;
  margin: 4rem 0;
  ${({ theme }) => theme.textStyles.p1};
`

const LoaderWrapper = styled.div`
  position: relative;
  left: 1.5rem;
  bottom: 0.5rem;
`
